import React from 'react';

import _ from 'lodash';
import { AIOutput, SaveSheet } from 'src/common/types/SaveSheet';

import {
  useCheckCodeCombinationsMutation,
  useLazySearchExtendedSurgeryLayer2Query,
  useLazySearchQuery,
} from '../../common/apis/FlorenceApi';
import { ICD10Code, SelectableCode } from '../../common/types/Codes';
import { Search } from '../../common/types/Search';

export const useCodesExtended = (
  sheet: SaveSheet | null,
  searchText: string,
  searchType: 'text' | 'scan',
  limit: number
) => {
  const [submitSearch, { data: results, isFetching: isLoading }] =
    useLazySearchQuery();

  const [
    searchExtendedSurgeryLayer2,
    {
      data: resultsExtendedSurgeryLayer2,
      isFetching: isLoadingExtendedSurgeryLayer2,
    },
  ] = useLazySearchExtendedSurgeryLayer2Query();

  const [incorrectCodeCombinations, setIncorrectCodeCombinations] =
    React.useState(false);

  const [checkCodeCombinations] = useCheckCodeCombinationsMutation();

  const [suggestedICD10, setSuggestedICD10] = React.useState<ICD10Code[]>([]);
  const [suggestedModifiers, setSuggestedModifiers] = React.useState<
    { cpt_code: string; modifier: string }[]
  >([]);
  const [suggestedUnits, setSuggestedUnits] = React.useState<
    { cpt_code: string; unit: number }[]
  >([]);

  const [AIOutput, setAIOutput] = React.useState<AIOutput | null>(null);

  const [importantCodes, setImportantCodes] = React.useState<string[]>([]);

  // Search Args triggering refetch
  const searchArgs: Search = React.useMemo(() => {
    return {
      text: searchText,
      sheetId: sheet?.sheetId,
      searchType,
      includeTerms: [],
      excludeTerms: [],
      limit,
      smartLaunch: false,
      forceSurgeryEngine: false,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchType]);

  // The main codes state
  const [codes, setCodes] = React.useState<SelectableCode[]>([]);

  const isArrayEqual = (x: any[], y: any[]) => {
    return _(x).xorWith(y, _.isEqual).isEmpty();
  };

  const updateCodes = (newCodes: SelectableCode[]) => {
    if (isArrayEqual(newCodes, codes)) return;
    let sortedCodes = newCodes;

    // sort the codes and keep primary codes at the top
    sortedCodes = _.sortBy(sortedCodes, [
      (c) => (c.isPrimaryCode ? 0 : 1),
      (c) => (c.manuallyAdded ? 0 : 1),
    ]);

    sortedCodes = _.uniqBy(sortedCodes, 'code');
    setCodes(sortedCodes);
  };

  const selectedCodes = React.useMemo(
    () => codes.filter((c) => c.selected),
    [codes]
  );

  // Main function refetching the codes
  const refetchCodes = React.useCallback(async () => {
    if (isLoadingExtendedSurgeryLayer2 || isLoading) return;
    if (_.isEmpty(searchArgs.text)) return;
    try {
      // If we have no codes, then it's the initial request and we send the regular API request
      // If we have codes, then we're on the Layer 2 (check codes button pressed) and we send a different API request
      if (selectedCodes.length > 0) {
        const response = await searchExtendedSurgeryLayer2({
          text: searchArgs.text,
          foundCodes: selectedCodes.map((c) => c.code),
          sheetId: sheet?.sheetId ?? 0,
        }).unwrap();

        setAIOutput({
          icd10: AIOutput?.icd10 ?? '',
          modifiers: response.cpt_modifiers_text ?? '',
          units: response.cpt_units_text ?? '',
          grants_choice: response.grants_choice ?? '',
        });

        if (response.cpt_modifiers) {
          setSuggestedModifiers(response.cpt_modifiers);
        }

        if (response.cpt_units) {
          setSuggestedUnits(response.cpt_units);
        }

        // Update existing codes with new modifiers and units in a single update
        const updatedCodes = codes.map((code) => {
          const updatedCode = { ...code };

          // Apply modifier if available
          const modifier = response.cpt_modifiers?.find(
            (m) => m.cpt_code === code.code
          );
          if (modifier) {
            updatedCode.modifiers = modifier.modifier
              ? [modifier.modifier]
              : code.modifiers;
          }

          // Apply unit if available
          const unit = response.cpt_units?.find(
            (u) => u.cpt_code === code.code
          );
          if (unit) {
            updatedCode.multiplier = unit.unit;
          }

          return updatedCode;
        });

        updateCodes(updatedCodes);

        // Return early to avoid the code in the useEffect from replacing our selected codes
        return;
      }

      const response = await submitSearch({ ...searchArgs }, false).unwrap();

      if (response.results.icd10_codes) {
        setSuggestedICD10(
          response.results.icd10_codes.map((c) => {
            const isSelected = response.results.important_codes
              ? response.results.important_codes.includes(c.code)
              : false;

            return {
              code: c.code,
              description: c.description,
              selected: isSelected,
              source: 'galeai',
            };
          })
        );
      }

      if (response.results.cpt_modifiers) {
        setSuggestedModifiers(response.results.cpt_modifiers);
      }

      if (response.results.cpt_units) {
        setSuggestedUnits(response.results.cpt_units);
      }

      if (
        response.results.icd10_text ||
        response.results.cpt_modifiers_text ||
        response.results.cpt_units_text ||
        response.results.grants_choice
      ) {
        setAIOutput({
          icd10: response.results.icd10_text ?? '',
          modifiers: AIOutput?.modifiers ?? '',
          units: AIOutput?.units ?? '',
          grants_choice: AIOutput?.grants_choice ?? '',
        });
      }

      if (response.results.important_codes) {
        setImportantCodes(response.results.important_codes);
      }
    } catch (error: unknown) {
      console.log(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchArgs, selectedCodes]);

  // Initialize the codes from the sheet
  React.useEffect(() => {
    if (sheet) {
      if (sheet.aiOutput) {
        setAIOutput(sheet.aiOutput);
      }

      // Only initialize codes from sheet if we don't already have codes
      // This prevents overwriting our Layer 2 updates
      if (sheet.sheetCodes && sheet.sheetCodes.length && codes.length === 0) {
        updateCodes(
          sheet.sheetCodes.map((c) => {
            return {
              ...c,
              selected: true,
            };
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet]);

  // Update the codes when the results are fetched
  React.useEffect(() => {
    if (isLoading || isLoadingExtendedSurgeryLayer2) return;

    // Skip processing for Layer 2 results if we already have selected codes
    // This prevents replacing user selections when "Check Codes" is pressed
    if (resultsExtendedSurgeryLayer2 && selectedCodes.length > 0) {
      return;
    }

    let primaryCode: SelectableCode | null = null;

    if (results?.results?.code_details && results?.results?.codes?.length) {
      primaryCode = {
        ...results.results?.code_details,
        selected: true,
        manuallyAdded: false,
        order: 999,
        isPrimaryCode: true,
        from: 'galeai',
        modifiers: getSuggestedModifiers(results.results?.code_details.code),
        multiplier: getSuggestedUnits(results.results?.code_details.code),
        icd10: [],
      };
    }

    let auxCodes = primaryCode ? [primaryCode] : results?.results?.codes ?? [];

    // Add ICD-10 codes if it's not a smart launch and we have suggested ICD-10 codes from the extended engine
    if (
      results?.results?.icd10_codes &&
      results?.results?.icd10_codes.length > 0
    ) {
      const icd10Codes: SelectableCode[] = results.results.icd10_codes.map(
        (icd10, index) => ({
          id: index.toString(),
          code: icd10.code,
          description: icd10.description,
          manuallyAdded: false,
          selected: findIfImportant(icd10.code),
          order: 999,
          isPrimaryCode: false,
          from: 'galeai',
          multiplier: 1,
          modifiers: [],
          icd10: [],
        })
      );

      auxCodes = [...auxCodes, ...icd10Codes];
    }

    if (suggestedModifiers) {
      auxCodes = auxCodes.map((c) => {
        return {
          ...c,
          modifiers: getSuggestedModifiers(c.code),
        };
      });
    }

    if (suggestedUnits) {
      auxCodes = auxCodes.map((c) => {
        return {
          ...c,
          multiplier: getSuggestedUnits(c.code),
        };
      });
    }

    const codesFromGaleAI: SelectableCode[] = _.chain(auxCodes)
      .map((c) =>
        _.assign({}, c, {
          selected:
            _.has(c, 'selected') && (c as SelectableCode).selected === true
              ? true
              : findIfImportant(c.code),
        })
      )
      .value();

    updateCodes(codesFromGaleAI);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, resultsExtendedSurgeryLayer2]);

  // Code updating functions
  const selectModifiers = (code: SelectableCode, modifiers: string[]) => {
    updateCodes(
      codes.map((c) => {
        if (c.code !== code.code) return c;
        else return { ...c, modifiers: modifiers };
      })
    );
  };

  const selectMultiplier = (code: SelectableCode, multiplier: number) => {
    updateCodes(
      codes.map((c) => {
        if (c.code !== code.code) return c;
        else return { ...c, multiplier: multiplier };
      })
    );
  };

  const selectCode = (code: SelectableCode) => {
    updateCodes(
      codes.map((c) => {
        if (c.code !== code.code) return c;
        else
          return {
            ...c,
            // toggle the selected state
            selected: !c.selected,
            // reset the invalidCombos if the code is unselected
            invalidCombos: c.selected ? [] : code.invalidCombos,
          };
      })
    );
  };

  const selectAllCodes = () => {
    updateCodes(
      codes.map((code) => {
        return { ...code, selected: true };
      })
    );
  };

  const clearSelectedCodes = () => {
    updateCodes(
      codes.map((code) => {
        return { ...code, selected: false };
      })
    );
  };

  const addManualCode = (code: SelectableCode) => {
    code.from = 'galeai';
    const codeIndex = codes.findIndex((c) => c.code === code.code);
    if (codeIndex !== -1) {
      updateCodes(
        codes.map((c) => {
          if (c.code !== code.code) return c;
          else return code;
        })
      );
    } else {
      updateCodes([code, ...codes]);
    }
  };

  const flagCode = (code: SelectableCode) => {
    updateCodes(
      codes.map((c) => {
        if (c.code !== code.code) return c;
        else
          return {
            ...c,
            isFlagged: !c.isFlagged,
          };
      })
    );
  };

  // Check Code Combinations Compatibility
  React.useEffect(() => {
    if (selectedCodes.length < 2) {
      // reset invalid combos
      setIncorrectCodeCombinations(false);
      updateCodes(
        codes.map((c) => {
          c.invalidCombos = [];
          return c;
        })
      );
      return;
    }

    checkCodeCombinations(selectedCodes.map((c) => c.code))
      .unwrap()
      .then((combos) => {
        const incorrect =
          combos.filter((c) => c.status === 'incorrect').length > 0;
        setIncorrectCodeCombinations(incorrect);

        const newCodes = codes.map((c) => {
          for (const combo of combos) {
            if (c.code === combo.code) {
              c.invalidCombos = combo.reasons;
            }
          }
          return c;
        });

        updateCodes(newCodes);
      })
      .catch((error) => {
        console.log(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCodes, codes, checkCodeCombinations]);

  const updateICD10 = (code: SelectableCode, newIcd10: ICD10Code[]) => {
    updateCodes(
      codes.map((c) => {
        if (c.code !== code.code) return c;
        else
          return {
            ...c,
            code: c.code,
            icd10: [...newIcd10],
          };
      })
    );
  };

  const getSuggestedModifiers = (code: string) => {
    return suggestedModifiers
      .filter((m) => m.cpt_code === code)
      .map((m) => m.modifier);
  };

  const getSuggestedUnits = (code: string) => {
    const suggestedUnit = suggestedUnits.find((m) => m.cpt_code === code);
    if (suggestedUnit) {
      return suggestedUnit.unit;
    }

    return 1;
  };

  const findIfImportant = (code: string) => {
    if (!importantCodes) return false;
    return importantCodes.includes(code);
  };

  return {
    codes,
    selectedCodes,
    isLoading: isLoading || isLoadingExtendedSurgeryLayer2,
    incorrectCodeCombinations,
    suggestedICD10,
    refetchCodes,
    selectModifiers,
    selectMultiplier,
    clearSelectedCodes,
    selectAllCodes,
    selectCode,
    addManualCode,
    flagCode,
    updateICD10,
    AIOutput,
  };
};
